import { useKeycloak } from '@react-keycloak/web';
import { ArrowRight, Compass, Download, ExternalLink, Mountain, Plus, Search, Sparkles } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { RegionHero, RegionTiles, regionDatasetHint } from '../components/RegionOverview.js';
import { api, type AlertEvent, type LinkTile, type SourceHealthAggregate, type SystemHealthResponse } from '../lib/api.js';
import { config as appConfig } from '../lib/config.js';
import { displayFirstName, greeting, recentEvents, relativeTime, type RecentEvent } from '../lib/overview.js';
import { realmRoles } from '../lib/roles.js';
import { cn } from '../lib/utils.js';
import { useWorkspace } from '../lib/workspace.js';

/**
 * Uebersicht nach dem Login: wie steht es, was braucht Aufmerksamkeit, was ist neu, und die
 * Link-Kacheln der Kommune als Einstieg in ihre Anwendungen. Liest nur, was CiviPort schon hat
 * (Datensaetze, Quellen, Meldungen, Systemzustand, Protokoll, Link-Kacheln); jede Kachel fuehrt
 * zur bestehenden Seite. Verwaltungssachen (Quellen, System, Protokoll) nur fuer admin.
 */
interface Lage {
  datasets: { total: number; public: number | null } | null;
  datasetsFailed?: boolean;
  sources: SourceHealthAggregate | null;
  alerts: AlertEvent[] | null;
  system: SystemHealthResponse | null;
}

export function OverviewPage(): JSX.Element {
  const { keycloak } = useKeycloak();
  const isAdmin = realmRoles(keycloak.tokenParsed).includes('admin');
  const name = displayFirstName(keycloak.tokenParsed as Record<string, unknown> | undefined);
  const now = new Date();
  // Gliederung: Titelbild, Zahlen und Gemeinde-Kacheln des Arbeitsbereichs.
  const ws = useWorkspace();

  const [lage, setLage] = useState<Lage>({ datasets: null, sources: null, alerts: null, system: null });
  const [tiles, setTiles] = useState<LinkTile[] | null>(null);
  const [events, setEvents] = useState<RecentEvent[] | null>(null);

  useEffect(() => {
    let alive = true;
    const set = (patch: Partial<Lage>): void => {
      if (alive) setLage((l) => ({ ...l, ...patch }));
    };
    api
      .listDatasets({ lifecycle: 'active', limit: 200 })
      .then((r) =>
        // Oeffentlich/intern nur, wenn alle Eintraege in einer Seite stehen (Katalog liefert hoechstens 200).
        set({ datasets: { total: r.total, public: r.total <= r.items.length ? r.items.filter((d) => d.access_rights === 'PUBLIC').length : null } }),
      )
      .catch(() => set({ datasets: { total: 0, public: null }, datasetsFailed: true }));
    api
      .listAlertEvents({ state: 'firing', limit: 20 })
      .then((r) => set({ alerts: r.items }))
      .catch(() => set({ alerts: [] }));
    api
      .listLinkTiles()
      .then((r) => alive && setTiles([...r.items].sort((a, b) => a.position - b.position)))
      .catch(() => alive && setTiles([]));
    if (isAdmin) {
      api.sourceHealth().then((r) => set({ sources: r.aggregate })).catch(() => undefined);
      api.getSystemHealth().then((r) => set({ system: r })).catch(() => undefined);
      const since = new Date(Date.now() - 24 * 3600 * 1000);
      api
        .listAuditLog({ limit: 200 })
        .then((r) => alive && setEvents(recentEvents(r.items, since)))
        .catch(() => alive && setEvents([]));
    }
    return () => {
      alive = false;
    };
  }, [isAdmin]);

  const firing = lage.alerts ?? [];
  const sourceErrors = lage.sources ? lage.sources.error + lage.sources.stale : 0;
  const systemDown = lage.system ? lage.system.services.filter((s) => s.status !== 'ok') : [];
  const attention = firing.length + (isAdmin ? (sourceErrors > 0 ? 1 : 0) + (systemDown.length > 0 ? 1 : 0) : 0);

  return (
    <div className="-mx-4 -mt-6 md:-mx-6" data-testid="uebersicht">
      {ws ? <RegionHero ws={ws} /> : null}
      <section className="border-b border-border bg-card">
        <div className="mx-auto max-w-6xl px-4 pt-8 md:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">
                {now.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
              <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">
                {greeting(now)}
                {name ? `, ${name}` : ''}.
              </h1>
              <p className="text-base text-muted-foreground">
                {attention === 0
                  ? 'Alles läuft. Nichts braucht gerade Ihre Aufmerksamkeit.'
                  : `${attention === 1 ? 'Eine Sache braucht' : `${attention} Dinge brauchen`} Ihre Aufmerksamkeit.`}
              </p>
            </div>
            <Link
              to="/dashboard"
              className="inline-flex min-h-11 items-center gap-2 rounded-md bg-primary px-4 font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              Mein Dashboard öffnen
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </div>

          <div role="group" className="mt-6 grid grid-cols-2 border-t border-border lg:grid-cols-4" aria-label="Lage">
            <LageZelle
              to="/catalog"
              label="Datensätze"
              value={ws ? String(ws.node.datasets.total) : lage.datasets && !lage.datasetsFailed ? String(lage.datasets.total) : '–'}
              hint={
                ws
                  ? regionDatasetHint(ws)
                  : lage.datasetsFailed
                  ? 'nicht abrufbar'
                  : !lage.datasets
                    ? 'wird geladen …'
                    : lage.datasets.public === null
                      ? 'aktive Einträge im Katalog'
                      : `${lage.datasets.public} öffentlich · ${lage.datasets.total - lage.datasets.public} intern`
              }
            />
            {isAdmin ? (
              <LageZelle
                to="/sources"
                label="Quellen"
                value={lage.sources ? `${lage.sources.ok}` : '–'}
                unit={lage.sources && lage.sources.total > 0 ? `von ${lage.sources.total} in Ordnung` : undefined}
                hint={
                  !lage.sources
                    ? 'wird geladen …'
                    : lage.sources.total === 0
                      ? 'noch keine angebunden'
                      : sourceErrors > 0
                        ? `${sourceErrors} mit Fehler oder überfällig`
                        : 'alle Abrufe pünktlich'
                }
                tone={sourceErrors > 0 ? 'bad' : undefined}
              />
            ) : (
              <LageZelle to="/gallery" label="Auswertungen" value="→" hint="Diagramme und Kennzahlen ansehen" />
            )}
            <LageZelle
              to="/alerts"
              label="Offene Meldungen"
              value={lage.alerts ? String(firing.length) : '–'}
              hint={firing.length === 0 ? 'keine offenen Meldungen' : `${firing.filter((a) => a.severity === 'critical').length} kritisch`}
              tone={firing.length > 0 ? 'bad' : undefined}
            />
            {isAdmin ? (
              <LageZelle
                to="/admin/system"
                label="System & Betrieb"
                value={!lage.system ? '–' : systemDown.length === 0 ? 'in Ordnung' : 'gestört'}
                hint={!lage.system ? 'wird geladen …' : systemDown.length === 0 ? 'alle Dienste erreichbar' : `${systemDown.length} Dienst(e) nicht erreichbar`}
                tone={!lage.system ? undefined : systemDown.length === 0 ? 'good' : 'bad'}
              />
            ) : (
              <LageZelle to="/twin" label="Digitaler Zwilling" value="3D" hint="Gelände, Gebäude und Pläne" />
            )}
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl space-y-10 px-4 py-8 md:px-8">
        {ws ? <RegionTiles ws={ws} /> : null}
        <section aria-labelledby="links-titel" className="space-y-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="space-y-1">
              <h2 id="links-titel" className="font-display text-2xl font-bold tracking-tight">
                Anwendungen &amp; Links
              </h2>
              <p className="text-sm text-muted-foreground">Die Link-Kacheln der Kommune: Einstieg in ihre Anwendungen und Portale.</p>
            </div>
            {isAdmin ? (
              <Link to="/admin/link-tiles" className="text-sm font-semibold text-primary underline-offset-4 hover:underline">
                Link-Kacheln verwalten
              </Link>
            ) : null}
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-5">
            {tiles === null
              ? [0, 1, 2].map((i) => <div key={i} className="h-64 animate-pulse rounded-md border border-border bg-muted" />)
              : tiles.map((t) => <LinkKachel key={t.id} tile={t} />)}
            {isAdmin ? (
              <Link
                to="/admin/link-tiles"
                className={cn(
                  'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input p-6 text-center text-muted-foreground transition-colors hover:border-primary hover:text-primary',
                  // Ohne Kacheln kein leerer Riesenkasten, mit Kacheln so hoch wie diese.
                  tiles && tiles.length > 0 ? 'min-h-64' : 'min-h-36',
                )}
              >
                <Plus className="h-7 w-7" aria-hidden />
                <span className="font-semibold">Link-Kachel hinzufügen</span>
                <span className="text-xs">nur für Administratoren</span>
              </Link>
            ) : null}
          </div>
          {tiles !== null && tiles.length === 0 && !isAdmin ? (
            <p className="text-sm text-muted-foreground">Die Kommune hat noch keine Link-Kacheln angelegt.</p>
          ) : null}
        </section>

        <div className="grid gap-6 lg:grid-cols-2">
          <Kasten titel="Braucht Aufmerksamkeit">
            {attention === 0 ? (
              <p className="text-sm text-muted-foreground">Nichts offen. Meldungen, gestörte Quellen und Dienste erscheinen hier.</p>
            ) : (
              <ul className="divide-y divide-border">
                {isAdmin && systemDown.length > 0 ? (
                  <Hinweis stufe="FEHLER" titel={`${systemDown.length} Dienst(e) nicht erreichbar`} text={systemDown.map((s) => s.service).join(', ')} to="/admin/system" aktion="System & Betrieb öffnen" />
                ) : null}
                {isAdmin && sourceErrors > 0 ? (
                  <Hinweis stufe="FEHLER" titel={`${sourceErrors} Quelle(n) mit Fehler oder überfällig`} text="Der letzte Abruf ist fehlgeschlagen oder zu lange her." to="/sources" aktion="Quellen prüfen" />
                ) : null}
                {firing.slice(0, 4).map((a) => (
                  <Hinweis
                    key={a.id}
                    stufe={a.severity === 'critical' ? 'FEHLER' : 'HINWEIS'}
                    titel={a.message}
                    text={`gemeldet ${relativeTime(a.fired_at)}`}
                    to="/alerts"
                    aktion="Zur Meldung"
                  />
                ))}
              </ul>
            )}
          </Kasten>

          {isAdmin ? (
            <Kasten titel="Neu seit gestern">
              {events === null ? (
                <p className="text-sm text-muted-foreground">wird geladen …</p>
              ) : events.length === 0 ? (
                <p className="text-sm text-muted-foreground">Seit gestern hat sich nichts geändert.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {events.map((e) => (
                    <li key={e.id} className="flex justify-between gap-3 py-2.5 text-sm">
                      <span>
                        {e.label}
                        {e.detail ? <span className="font-semibold">: {e.detail}</span> : null}
                      </span>
                      <span className="whitespace-nowrap text-muted-foreground">{relativeTime(e.at)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <Link to="/audit" className="mt-3 inline-block text-sm font-semibold text-primary underline-offset-4 hover:underline">
                Ganzes Protokoll
              </Link>
            </Kasten>
          ) : null}
        </div>

        <section aria-labelledby="los-titel" className="space-y-3">
          <h2 id="los-titel" className="font-display text-xl font-bold tracking-tight">
            Schnell loslegen
          </h2>
          <div className="flex flex-wrap gap-3">
            {isAdmin ? <Schnell to="/sources/new" icon={<Download className="h-5 w-5" />} label="Daten einbinden" /> : null}
            <Schnell to="/builder" icon={<Sparkles className="h-5 w-5" />} label="Mit KI erstellen" />
            <Schnell to="/explore" icon={<Compass className="h-5 w-5" />} label="Selbst zusammenstellen" />
            <Schnell to="/twin" icon={<Mountain className="h-5 w-5" />} label="Digitaler Zwilling" />
            <Schnell to="/catalog" icon={<Search className="h-5 w-5" />} label="Datensätze durchsuchen" />
          </div>
        </section>
      </div>
    </div>
  );
}

function LageZelle({ to, label, value, unit, hint, tone }: { to: string; label: string; value: string; unit?: string; hint: string; tone?: 'good' | 'bad' }): JSX.Element {
  return (
    <Link
      to={to}
      className="flex flex-col gap-0.5 border-l border-border py-4 pl-5 pr-4 transition-colors first:border-l-0 first:pl-0 hover:bg-muted/50"
    >
      <span className="text-sm font-semibold text-muted-foreground">{label}</span>
      <span className={`font-display text-3xl font-bold tracking-tight ${tone === 'good' ? 'text-[#00703c] dark:text-emerald-400' : ''}`}>
        {value}
        {unit ? <span className="ml-1.5 text-base font-medium text-muted-foreground">{unit}</span> : null}
      </span>
      <span className={`text-xs ${tone === 'bad' ? 'font-semibold text-destructive' : 'text-muted-foreground'}`}>{hint}</span>
    </Link>
  );
}

function LinkKachel({ tile }: { tile: LinkTile }): JSX.Element {
  let host = tile.url;
  try {
    host = new URL(tile.url).host;
  } catch {
    // relative Adresse: so lassen
  }
  return (
    <a
      href={tile.url}
      target={tile.open_in_tab ? '_blank' : '_self'}
      rel={tile.open_in_tab ? 'noopener noreferrer' : undefined}
      className="group flex flex-col overflow-hidden rounded-md border border-border bg-card text-card-foreground transition-shadow hover:border-foreground/30 hover:shadow-md"
      data-testid="link-kachel"
    >
      <div className="aspect-[16/9] w-full overflow-hidden bg-muted">
        <img
          src={`${appConfig.apiBaseUrl}/api/catalog/link-tiles/${tile.id}/image`}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
        />
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="text-lg font-bold leading-snug">{tile.title}</span>
          {tile.open_in_tab ? <ExternalLink className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" aria-label="öffnet in neuem Tab" /> : null}
        </div>
        {tile.subtitle ? <span className="text-sm leading-relaxed text-muted-foreground">{tile.subtitle}</span> : null}
        <span className="mt-auto truncate pt-1 text-xs text-muted-foreground">{host}</span>
      </div>
    </a>
  );
}

function Kasten({ titel, children }: { titel: string; children: ReactNode }): JSX.Element {
  return (
    <section className="rounded-md border border-border bg-card p-5 md:p-6">
      <h2 className="mb-3 font-display text-xl font-bold tracking-tight">{titel}</h2>
      {children}
    </section>
  );
}

function Hinweis({ stufe, titel, text, to, aktion }: { stufe: 'FEHLER' | 'HINWEIS'; titel: string; text: string; to: string; aktion: string }): JSX.Element {
  return (
    <li className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span
        className={`shrink-0 rounded-sm px-2 py-0.5 text-[11px] font-bold ${stufe === 'FEHLER' ? 'bg-destructive text-destructive-foreground' : 'bg-[#ffdd00] text-[#0b0c0c]'}`}
      >
        {stufe}
      </span>
      <div className="min-w-0 space-y-0.5">
        <p className="font-semibold">{titel}</p>
        <p className="text-sm text-muted-foreground">{text}</p>
        <Link to={to} className="text-sm font-semibold text-primary underline-offset-4 hover:underline">
          {aktion}
        </Link>
      </div>
    </li>
  );
}

function Schnell({ to, icon, label }: { to: string; icon: ReactNode; label: string }): JSX.Element {
  return (
    <Link
      to={to}
      className="inline-flex min-h-12 items-center gap-2.5 rounded-md border border-border bg-card px-4 font-semibold transition-colors hover:border-primary hover:text-primary [&_svg]:text-primary"
    >
      {icon}
      {label}
    </Link>
  );
}
